import React from "react";
import {
  Box, Typography, Chip, Stack, Button, Alert, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, alpha, useTheme,
} from "@mui/material";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { paymentsApi } from "../../Parent/Detail/payments/api";

/**
 * Admin reconciliation queue.
 * Backing endpoint: GET /admin/payments/reconciliation
 */
const ReconciliationQueue: React.FC = () => {
  const t = useTheme();
  const [rows, setRows] = React.useState<any[]>([]);
  const [status, setStatus] = React.useState<"open" | "resolved" | "ignored" | "all">("open");
  const [operation, setOperation] = React.useState<string>("all");
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [resolveTarget, setResolveTarget] = React.useState<{ id: number; action: "resolved" | "ignored" } | null>(null);
  const [notes, setNotes] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const fetchAll = React.useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params: any = {};
      if (status !== "all") params.status = status;
      if (operation !== "all") params.operation = operation;
      const r = await paymentsApi.reconciliationList(params);
      setRows(r?.data ?? []);
    } catch (e: any) { setError(e?.message ?? "Failed to load"); }
    finally { setLoading(false); }
  }, [status, operation]);

  React.useEffect(() => { fetchAll(); }, [fetchAll]);

  const submit = async () => {
    if (!resolveTarget) return;
    setBusy(true);
    try {
      await paymentsApi.reconciliationResolve(resolveTarget.id, resolveTarget.action, notes);
      setResolveTarget(null); setNotes("");
      fetchAll();
    } catch (e: any) { setError(e?.message ?? "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <ReportProblemOutlinedIcon color="warning" />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Payment Reconciliation Queue</Typography>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField select label="Status" value={status} onChange={(e) => setStatus(e.target.value as any)} size="small" sx={{ minWidth: 160 }}>
          {["open", "resolved", "ignored", "all"].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <TextField select label="Operation" value={operation} onChange={(e) => setOperation(e.target.value as any)} size="small" sx={{ minWidth: 200 }}>
          {["all", "add_card", "charge", "delete_card", "update_card", "verify_profile", "other"].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
      </Stack>

      <TableContainer sx={{ background: t.palette.background.paper, border: `1px solid ${alpha(t.palette.divider, 0.6)}`, borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Operation</TableCell>
              <TableCell>Parent</TableCell>
              <TableCell>Payment Attempt</TableCell>
              <TableCell>Provider Txn</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8}><Typography variant="body2">Loading…</Typography></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={8}><Alert severity="info">No cases in this view.</Alert></TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{r.created_at?.slice(0, 19).replace("T", " ")}</TableCell>
                <TableCell><strong>{r.operation}</strong></TableCell>
                <TableCell>{r.parent_first ? `${r.parent_first} ${r.parent_last ?? ""}`.trim() : "—"}</TableCell>
                <TableCell>#{r.payment_attempt_id ?? "—"}</TableCell>
                <TableCell>{r.provider_transaction_id ?? "—"}</TableCell>
                <TableCell>{r.reason}</TableCell>
                <TableCell><Chip size="small" label={r.status} color={r.status === "open" ? "warning" : "default"} /></TableCell>
                <TableCell align="right">
                  {r.status === "open" && (
                    <>
                      <Button size="small" onClick={() => setResolveTarget({ id: r.id, action: "resolved" })}>Resolve</Button>
                      <Button size="small" color="inherit" onClick={() => setResolveTarget({ id: r.id, action: "ignored" })}>Ignore</Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {resolveTarget && (
        <Dialog open onClose={busy ? undefined : () => setResolveTarget(null)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>
            {resolveTarget.action === "resolved" ? "Resolve Case" : "Ignore Case"}
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {resolveTarget.action === "resolved"
                ? "Confirm the underlying provider state has been checked. Do not resolve until reconciled with Worldline."
                : "Ignoring will archive this case without provider verification."}
            </Alert>
            <TextField fullWidth multiline minRows={3} label="Resolution notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setResolveTarget(null)} disabled={busy}>Cancel</Button>
            <Button variant="contained" onClick={submit} disabled={busy}>
              {busy ? "Saving…" : resolveTarget.action === "resolved" ? "Mark Resolved" : "Ignore"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default ReconciliationQueue;
